import * as React from "react";
import {navigateTo} from "../../../navigateTo";
import {FormattedMessage} from "react-intl";
import {setCurrentTabAction} from "../portfolio/PortfolioActions";
import {PortfolioTab} from "../portfolio/PortfolioPage";
import {useDispatch} from "react-redux";
import {useEffect, useState} from "react";
import {IPlanAssetTrend, PortfolioApi} from "../../api/PortfolioApi";
import {calculateAssetTrend, IHomeAssetTrend} from "./HomeUtils";
import {useNavigate} from "react-router-dom";
import {AnyThunkDispatch} from "../../../commonTypes";
import {getTranslation} from "../../utils/translationUtil";


const isNegativeValue = (value: string | null | undefined): boolean => {
    return !!value && value.toString().startsWith("-");
};

const getValueClass = (value: string | null | undefined): string => {
    if (!value) return "";
    return isNegativeValue(value) ? "home__assets-value--negative" : "home__assets-value--positive";
};

export const HomeAssetTrends: React.FunctionComponent = () => {
    const [assetTrends, setAssetTrends] = useState<IPlanAssetTrend[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const dispatch: AnyThunkDispatch = useDispatch();

    useEffect(() => {
        PortfolioApi.getCurrentPlanAssetTrends()
            .then(response => setAssetTrends(response))
            .finally(() => setIsLoading(false));
    }, []);

    const handleNavigateToAssets = () => {
        dispatch(setCurrentTabAction(PortfolioTab.ASSET_ALLOCATION));
        navigateTo("/portfolio", navigate);
    };

    const renderTimePeriod = (timePeriod: IHomeAssetTrend["timePeriod"]) => {
        if (!timePeriod) return null;
        const translationId = `filters.${timePeriod.quarter}`;
        return <><FormattedMessage id={translationId} defaultMessage={timePeriod.quarter}/> {timePeriod.year}</>;
    };

    const renderTableRow = (
        label: string,
        recentValue: string,
        priorValue: string | undefined,
        colorize: boolean = false,
    ) => {
        return <tr>
            <td className="home-asset-trends__table-label">{label}</td>
            <td className={`home-asset-trends__table-value ${colorize ? getValueClass(recentValue) : ""}`}>
                {recentValue}
            </td>
            {priorValue !== undefined && (
                <td className={`home-asset-trends__table-value ${colorize ? getValueClass(priorValue) : ""}`}>
                    {priorValue}
                </td>
            )}
        </tr>;
    };

    if (isLoading) {
        return (
            <section className="home-asset-trends__container" data-testid="home-asset-trends__skeleton-container">
                <div className="home-asset-trends__inner">
                    <div className="home-asset-trends__skeleton" aria-busy="true" />
                </div>
            </section>
        );
    }

    if (!assetTrends || assetTrends.length === 0) {
        return null;
    }

    const recentAssetTrend = calculateAssetTrend(assetTrends[0], assetTrends[1]);
    const previousAssetTrend = calculateAssetTrend(assetTrends[1], assetTrends[2]);

    if (!recentAssetTrend) {
        return null;
    }

    const displayInflowOutFlow = recentAssetTrend.hasInFlowOutFlowValues
        && (!previousAssetTrend || previousAssetTrend.hasInFlowOutFlowValues);

    const changeIsNegative = isNegativeValue(recentAssetTrend.changeInMarketValue);

    const renderAssetTrends = () => {
        return <div className="home-asset-trends__hero" data-testid="home-asset-trends__hero">
            <div className="home-asset-trends__hero-header">
                <div className="home-asset-trends__hero-icon">
                    <i className="fa-light fa-wave-pulse" />
                </div>
                <h2 className="home-asset-trends__hero-title">
                    {getTranslation("header.portfolio-snapshot", "Portfolio Snapshot")}
                </h2>
            </div>

            <div className="home-asset-trends__hero-label">Ending Market Value</div>
            <div className="home-asset-trends__hero-value">{recentAssetTrend.endingMarketValue}</div>

            <div className="home-asset-trends__hero-label" style={{marginTop: "16px"}}>
                Change In Market Value
            </div>
            <div className="home-asset-trends__hero-change-row">
                            <span className={
                                `home-asset-trends__hero-change-value 
                                ${changeIsNegative
                                    ? "home-asset-trends__hero-change-value--negative"
                                    : "home-asset-trends__hero-change-value--positive"}`}>
                                {recentAssetTrend.changeInMarketValue}
                            </span>
                <span className={
                    `home-asset-trends__hero-percent-badge 
                                ${changeIsNegative ? "home-asset-trends__hero-percent-badge--negative" : ""}`}>
                                {changeIsNegative ? (
                                    <i className="fa-solid fa-arrow-down" />
                                ) : (
                                    <i className="fa-solid fa-arrow-up" />
                                )}
                    {recentAssetTrend.percentChange}
                            </span>
            </div>

            <div className="home-asset-trends__hero-period">
                {renderTimePeriod(recentAssetTrend.timePeriod)}
            </div>

            <button
                className="home-asset-trends__hero-link"
                onClick={handleNavigateToAssets}
                type="button"
            >
                View Asset Allocation
                <i className="fa-solid fa-chevron-right" />
            </button>
        </div>;
    };

    const renderAssetValueTrends = () => {
        return <div className="home-asset-trends__table-container">
            <div className="home-asset-trends__table-title">Asset Value Trends</div>
            <table className="home-asset-trends__table">
                <thead>
                <tr>
                    <th />
                    <th>{renderTimePeriod(recentAssetTrend.timePeriod)}</th>
                    {previousAssetTrend && <th>{renderTimePeriod(previousAssetTrend.timePeriod)}</th>}
                </tr>
                </thead>
                <tbody>
                {renderTableRow(
                    "Beginning Market Value",
                    recentAssetTrend.beginningMarketValue,
                    previousAssetTrend?.beginningMarketValue,
                )}
                {displayInflowOutFlow && renderTableRow(
                    "Inflows",
                    recentAssetTrend.totalInflows!,
                    previousAssetTrend?.totalInflows || undefined,
                    true,
                )}
                {displayInflowOutFlow && renderTableRow(
                    "Outflows",
                    recentAssetTrend.totalOutflows!,
                    previousAssetTrend?.totalOutflows || undefined,
                    true,
                )}
                {displayInflowOutFlow && renderTableRow(
                    "Investment Earnings",
                    recentAssetTrend.investmentEarnings!,
                    previousAssetTrend?.investmentEarnings || undefined,
                    true,
                )}
                {renderTableRow(
                    "Ending Market Value",
                    recentAssetTrend.endingMarketValue,
                    previousAssetTrend?.endingMarketValue,
                )}
                {renderTableRow(
                    "Percent Change",
                    recentAssetTrend.percentChange,
                    previousAssetTrend?.percentChange,
                    true,
                )}
                </tbody>
            </table>
        </div>;
    };

    return (
        <section className="home-asset-trends__container" data-testid="home-asset-trends__container">
            <div className="home-asset-trends__inner">
                <div className="home-asset-trends__card">
                    {renderAssetTrends()}
                    {renderAssetValueTrends()}
                </div>
            </div>
        </section>
    );
};
